import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Accordion, AccordionItem } from '@cbhq/cds-web/accordion';
import { Button } from '@cbhq/cds-web/buttons';
import { TextInput } from '@cbhq/cds-web/controls';
import { LogoMark } from '@cbhq/cds-web/icons/LogoMark';
import { Divider, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextTitle2 } from '@cbhq/cds-web/typography';
import { TextHeadline } from '@cbhq/cds-web/typography/TextHeadline';

const COOKIE_MAX_AGE_SECONDS = 86400;

function setCookieForLocalhost(name: string, value: string) {
  document.cookie = `${name}=${value}; Path=/; Domain=localhost; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Secure;`;
}

export function LocalDevSignin() {
  const [searchParams] = useSearchParams();
  const redirectUrl = useMemo(() => searchParams.get('redirectUrl') || '/team', [searchParams]);

  const [sessionCookie, setSessionCookie] = useState('');
  const [gsscCookie, setGsscCookie] = useState('');

  const handleOAuthSignin = useCallback(() => {
    window.location.assign('/login');
  }, []);

  const handleSessionCookieChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionCookie(e.target.value);
  }, []);

  const handleGsscCookieChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setGsscCookie(e.target.value);
  }, []);

  const handleManualSubmit = useCallback(() => {
    if (!sessionCookie || !gsscCookie) {
      return;
    }

    setCookieForLocalhost('unified-session-manager-cookie', sessionCookie);
    setCookieForLocalhost('cb-gssc_development', gsscCookie);
    window.location.assign(redirectUrl);
  }, [sessionCookie, gsscCookie, redirectUrl]);

  const isManualSubmitDisabled = useMemo(
    () => !sessionCookie || !gsscCookie,
    [sessionCookie, gsscCookie],
  );

  return (
    <VStack alignItems="center" height="100vh" paddingTop={10} gap={6}>
      <VStack alignItems="center" gap={2}>
        <HStack alignItems="center" gap={1}>
          <LogoMark size={32} />
          <TextHeadline as="span" font="title4" textTransform="uppercase">
            Workspace
          </TextHeadline>
        </HStack>
        <TextTitle2 as="h1" display="block">
          Local Development Sign In
        </TextTitle2>
      </VStack>

      <VStack alignItems="center" justifyContent="center" gap={4} width={400}>
        <VStack gap={2}>
          <Button onClick={handleOAuthSignin} accessibilityLabel="Sign in with OAuth">
            Sign in with OAuth
          </Button>
        </VStack>

        <Divider />

        <Accordion>
          <AccordionItem title="Manual Cookie Paste" itemKey="manual">
            <VStack gap={2}>
              <TextBody as="p" color="fgMuted">
                If OAuth is unavailable, log in at accounts-dev.cbhq.net in another tab, copy the
                cookies from DevTools, and paste them below.
              </TextBody>
              <TextInput
                label="unified-session-manager-cookie"
                accessibilityLabel="Session manager cookie value"
                placeholder="Paste cookie value"
                value={sessionCookie}
                onChange={handleSessionCookieChange}
              />
              <TextInput
                label="cb-gssc_development"
                accessibilityLabel="GSSC development cookie value"
                placeholder="Paste cookie value"
                value={gsscCookie}
                onChange={handleGsscCookieChange}
              />
              <Button
                variant="secondary"
                onClick={handleManualSubmit}
                disabled={isManualSubmitDisabled}
                accessibilityLabel="Set cookies and continue"
              >
                Set Cookies & Continue
              </Button>
            </VStack>
          </AccordionItem>
        </Accordion>
      </VStack>
    </VStack>
  );
}
